import { useEffect, useState } from "react";
import api from "../Services/api";
import {
  FaHandHoldingHeart,
  FaMapMarkerAlt,
  FaSpinner,
  FaUsers,
} from "react-icons/fa";

const Donators = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDonations();
    // eslint-disable-next-line
  }, []);

  const fetchDonations = async () => {
    setLoading(true);
    try {
      const res = await api.get("/user/donators.php", {
        params: { page: 1, limit: 100 },
      });
      setList(res.data?.data?.list || []);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to load donators list");
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  const getFirstPhoto = (photoString) => {
    if (!photoString) return "";
    return photoString.split(",")[0].trim();
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  const totalRaised = list.reduce(
    (sum, d) => sum + (Number(d.donated_amount) || 0),
    0,
  );

  return (
    <div className="donators-page">
      {/* ── PAGE HERO ── */}
      <section className="about-hero">
        <div className="about-hero-bg" />
        <div className="container">
          <div className="about-hero-inner">
            <p className="about-eyebrow">Our Supporters</p>
            <h1>
              Our <span>Donators</span>
            </h1>
            <p className="about-hero-sub">
              Meet the generous hearts behind our mission, whose contributions
              help us create meaningful impact and support communities in need.
            </p>
          </div>
        </div>
      </section>

      {/* ── MAIN SECTION ── */}
      <section className="donators-section">
        <div className="container">
          {/* Stats bar */}
          {!loading && list.length > 0 && (
            <div className="donators-stats">
              <div className="donators-stat">
                <FaUsers />
                <div>
                  <strong>{list.length}</strong>
                  <span>Total Donators</span>
                </div>
              </div>
              <div className="donators-stat-divider" />
              <div className="donators-stat">
                <FaHandHoldingHeart />
                <div>
                  <strong>₹{totalRaised.toLocaleString("en-IN")}</strong>
                  <span>Total Contributed</span>
                </div>
              </div>
            </div>
          )}

          {/* States */}
          {loading ? (
            <div className="donators-loading">
              <FaSpinner className="donators-spinner" />
              <p>Loading donators…</p>
            </div>
          ) : list.length === 0 ? (
            <div className="donators-empty">
              <FaHandHoldingHeart />
              <p>No donations made yet.</p>
            </div>
          ) : (
            <>
              {/* ── Desktop Table ── */}
              <div className="donators-table-wrap">
                <table className="donators-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Donator</th>
                      <th>Amount</th>
                      <th>Description</th>
                      <th>Location</th>
                      <th>Photo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((d, index) => {
                      const firstPhoto = getFirstPhoto(d.photo);
                      return (
                        <tr key={d.id}>
                          <td className="donators-td-num">{index + 1}</td>

                          <td className="donators-td-name">
                            <div className="donators-avatar">
                              {getInitials(d.donor_name)}
                            </div>
                            <span>{d.donor_name || "—"}</span>
                          </td>

                          <td className="donators-td-amount">
                            ₹
                            {Number(d.donated_amount || 0).toLocaleString(
                              "en-IN",
                            )}
                          </td>

                          <td className="donators-td-desc">
                            {(d.description || "").slice(0, 90)}
                            {(d.description || "").length > 90 ? "…" : ""}
                          </td>

                          <td className="donators-td-location">
                            {d.location ? (
                              <span className="donators-location-pill">
                                <FaMapMarkerAlt /> {d.location}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>

                          <td className="donators-td-photo">
                            {firstPhoto ? (
                              <img
                                // src={`http://localhost/trust_site/uploads/${firstPhoto}`}
                                src={`https://geethaalagappantrust.com/trust_site/uploads/${firstPhoto}`}
                                alt="proof"
                                className="donators-photo-thumb"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <span className="donators-no-photo">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── Mobile Cards ── */}
              <div className="donators-cards">
                {list.map((d, index) => {
                  const firstPhoto = getFirstPhoto(d.photo);
                  return (
                    <div className="donators-card" key={d.id}>
                      <div className="donators-card-top">
                        <div className="donators-card-identity">
                          <div className="donators-avatar lg">
                            {firstPhoto ? (
                              <img
                                // src={`http://localhost/trust_site/uploads/${firstPhoto}`}
                                src={`https://geethaalagappantrust.com/trust_site/uploads/${firstPhoto}`}
                                alt={d.donor_name}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.parentNode.textContent = getInitials(
                                    d.donor_name,
                                  );
                                }}
                              />
                            ) : (
                              getInitials(d.donor_name)
                            )}
                          </div>
                          <div>
                            <strong className="donators-card-name">
                              {d.donor_name || "—"}
                            </strong>
                            {d.location && (
                              <span className="donators-location-pill sm">
                                <FaMapMarkerAlt /> {d.location}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="donators-card-amount">
                          <span>Donated</span>
                          <strong>
                            ₹
                            {Number(d.donated_amount || 0).toLocaleString(
                              "en-IN",
                            )}
                          </strong>
                        </div>
                      </div>
                      {d.description && (
                        <p className="donators-card-desc">
                          {(d.description || "").slice(0, 130)}
                          {(d.description || "").length > 130 ? "…" : ""}
                        </p>
                      )}
                      <span className="donators-card-index">#{index + 1}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Donators;
